import classNames from 'classnames';
import { useState, useEffect } from 'react';

import Loader from 'hb-react/shared/components/Loader';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import PaypalError from './errors/PaypalError';

const PaymentPaypal = ({
  commit,
  createOrderUrl,
  executePaymentUrl,
  payment_type: paymentType,
  payment_type_id: paymentTypeId,
  paymentSuccessCallback,
  amount,
  currency,
  additionalData,
  failCallback,
  paymentData,
  executePaymentCallback,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const getClientToken = () =>
    axios.post(Routes.paypal_client_token_path(), {
      payment_type: paymentType,
      payment_type_id: paymentTypeId,
    });

  const loadPayPalSDK = (clientId, clientToken) => {
    const disableFunding =
      'bancontact,blik,eps,giropay,mercadopago,mybank,p24,sepa,sofort,venmo,credit';
    const paypalScript =
      `https://www.paypal.com/sdk/js` +
      `?components=hosted-fields,buttons` +
      `&client-id=${clientId}` +
      `&disable-funding=${disableFunding}` +
      `&currency=${currency.code}`;

    const script = document.createElement('script');

    script.setAttribute('src', paypalScript);
    script.setAttribute('data-client-token', clientToken);
    script.setAttribute('data-namespace', 'PayPalSDK');
    script.setAttribute('async', false);

    return script;
  };

  const createOrder = (_data, _actions) => {
    const callData = {
      payment_type: paymentType,
      payment_type_id: paymentTypeId,
      amount,
      currency: currency.code,
    };

    return axios
      .post(createOrderUrl, callData)
      .then(({ data }) => data.id)
      .catch((error) => {
        if (failCallback) {
          failCallback(error);
        }
        errorReporting.captureException(
          new PaypalError('Error creating Paypal Order', error),
        );
      });
  };

  const onApprove = (data, _actions) => {
    setIsLoading(true);

    if (executePaymentUrl) {
      const paypalPayload = {
        gateway: 'Paypal',
        transaction_token: data.orderID,
        payer_id: data.payerID,
        id: paymentTypeId,
      };

      const callData =
        additionalData instanceof Function
          ? additionalData(paypalPayload)
          : {
              ...additionalData,
              ...paymentData,
              ...paypalPayload,
            };

      return axios
        .post(executePaymentUrl, callData)
        .then((res) => {
          paymentSuccessCallback(res);
        })
        .catch((error) => {
          if (failCallback) {
            failCallback(error);
          }

          errorReporting.captureException(
            new PaypalError('Error creating Paypal Order', error),
          );
          setIsLoading(false);
        });
    }

    if (executePaymentCallback) {
      return executePaymentCallback(data.orderID);
    }

    if (failCallback) {
      failCallback();
    }

    return errorReporting.captureException(
      new PaypalError('Error executing Paypal payment'),
    );
  };

  const createPaypalButtons = () =>
    window.PayPalSDK.Buttons({
      commit,
      createOrder,
      onApprove,
    });

  useEffect(() => {
    setIsLoading(true);
    getClientToken()
      .then(({ data }) => {
        const { client_id: clientId, client_token: clientToken } = data;
        const script = loadPayPalSDK(clientId, clientToken);

        document.head.appendChild(script);

        script.addEventListener('load', () => {
          createPaypalButtons().render('#paypal-button-container');
          setIsLoading(false);
        });
      })
      .catch((error) => {
        if (failCallback) {
          failCallback(error);
        }
        errorReporting.captureException(
          new PaypalError('Error loading PayPalSDK', error),
        );
      });
  }, []);

  return (
    <div>
      {isLoading && <Loader small={false} />}
      <div
        data-testid="payment-paypal"
        id="paypal-button-container"
        className={classNames({
          'deprecated--visuallyhidden': isLoading,
          'paypal-loaded': !isLoading,
        })}
      />
    </div>
  );
};

export default PaymentPaypal;
